import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { analyticsApi, ApiRequestError, type GapsDto } from "../../api/api";
import {
  AnalyticsFilterBar,
  lineTitle,
  type AnalyticsFiltersState,
} from "../../components/ui/AnalyticsFilterBar";
import { PageHeader } from "../../components/ui/PageHeader";

export function GapAnalysisPage() {
  const { t } = useTranslation();
  const [filters, setFilters] = useState<AnalyticsFiltersState>({
    year: new Date().getFullYear(),
  });
  const [data, setData] = useState<GapsDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    void analyticsApi
      .gaps(filters)
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch((err) => {
        if (!cancelled) {
          setData(null);
          setError(err instanceof ApiRequestError ? err.message : t("analytics_load_failed"));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [filters, t]);

  return (
    <div className="sm-page">
      <PageHeader title={t("gap_analysis")} />
      <AnalyticsFilterBar filters={filters} onChange={setFilters} />
      {error ? <p className="sm-error">{error}</p> : null}
      {loading ? (
        <p className="sm-muted">{t("loading")}</p>
      ) : (
        <div className="sm-card sm-card-pad">
          {data ? (
            <p className="sm-muted" style={{ marginTop: 0 }}>
              {t("target_threshold")}: <strong>{data.target}</strong>
            </p>
          ) : null}
          <div className="sm-table-wrap">
            <table className="sm-table">
              <thead>
                <tr>
                  <th>Line</th>
                  <th>{t("domain")}</th>
                  <th>{t("complexity_index")}</th>
                  <th>Avg</th>
                  <th>{t("target_threshold")}</th>
                  <th>{t("gap")}</th>
                  <th>#</th>
                </tr>
              </thead>
              <tbody>
                {(data?.gaps ?? []).map((g) => {
                  const { title, subtitle } = lineTitle(g.line);
                  return (
                    <tr key={g.line.id}>
                      <td>
                        <div>{title}</div>
                        <div className="sm-muted" style={{ fontSize: 11 }}>
                          {subtitle}
                        </div>
                      </td>
                      <td>{g.line.domain}</td>
                      <td style={{ textAlign: "center" }}>{g.line.complexityIndex}</td>
                      <td style={{ textAlign: "center" }}>{g.avg}</td>
                      <td style={{ textAlign: "center" }}>{g.target}</td>
                      <td style={{ textAlign: "center" }}>
                        <span className="sm-badge-pill sm-badge-red">-{g.gap}</span>
                      </td>
                      <td style={{ textAlign: "center" }}>
                        {g.belowCount}/{g.total}
                      </td>
                    </tr>
                  );
                })}
                {data && data.gaps.length === 0 ? (
                  <tr>
                    <td colSpan={7} style={{ textAlign: "center", color: "#059669" }}>
                      {t("gaps_none")}
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
